import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as QRIcon } from '../../assets/icons/action-qr.svg';
import { ReactComponent as CCS1ICON } from '../../assets/icons/device-management/ccs1.svg';
import { ReactComponent as CCS2Icon } from '../../assets/icons/device-management/ccs2.svg';
import { ReactComponent as CHADEMOIcon } from '../../assets/icons/device-management/chademo.svg';
import { ReactComponent as J1172ICON } from '../../assets/icons/device-management/j1172.svg';
import { ReactComponent as NACSICON } from '../../assets/icons/device-management/nacs.svg';
import { ReactComponent as Type2Icon } from '../../assets/icons/device-management/type2.svg';
import { ReactComponent as LinkIcon } from '../../assets/icons/link.svg';
import config from '../../config/config';
import { PortalType } from '../../config/enum';
import { useBranding } from '../../contexts/BrandingContext';
import { usePortal } from '../../contexts/PortalContext/PortalContext';
import DeviceQrCode from '../../pages/charger-pages/charger-management/components/DeviceQrCode';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setNotificationDetails } from '../../redux/slices/NotificationSlice';
import getFirstSubdomain from '../../shared-functions/DomainsplitFunction';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import deviceApi from '../../utils/api/partner/device-partner';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import ModalBox from '../modal-box/ModalBox';

import SidePaneModel from './SidePaneModel';
import TariffSidePanel from './TariffSidePanel';

interface Connector {
  connectorId: number;
  isReservationEnabled?: boolean;
  status?: string;
  _id?: string;
  isAvailable?: boolean;
  connectorType?: string[];
  connectorFormat?: string;
  connectorRating?: any;
  currentType?: string;
  maxCurrent?: string;
  maxVoltage?: string;
  maxPower?: string;
  unit?: string;
  tariffName?: string;
  tariffId?: string;
}

interface ConnectorSidePanelProperties {
  fetchIdFrom?: 'queryParams' | 'props';
  id?: string;
  connectors?: Connector[];
  currentType?: string;
  showHeader?: boolean;
  showActions?: boolean;
  onEditClick?: (connector: any, index: number) => void;
  chargingPointSn?: string;
}

interface ChargingPointProperties {
  _id?: string;
  chargingStation?: string;
  chargingPointName?: string;
  chargingPointSn?: string;
  connectors: Connector[];
  currentType?: string;
}

const connectorIcons = {
  Type1: J1172ICON,
  Type2: Type2Icon,
  CCS1: CCS1ICON,
  CCS2: CCS2Icon,
  CHADEMO: CHADEMOIcon,
  NACS: NACSICON,
};

/**
 * Interface for ModalBoxDetail properties.
 */
interface ModalBoxDetail {
  /**
   * The title of the modal.
   */
  title: string;
  /**
   * Whether the header is shown.
   */
  header: boolean;
  /**
   * The content element of the modal.
   */
  content: JSX.Element | null;
  /**
   * Whether the modal is a notification modal.
   */
  notificationModal: boolean;
}

/**
 * ConnectorSidePanel component.
 * Shows connector details for a specific device.
 * @param {ConnectorSidePanelProperties} properties - The properties
 * @returns {React.JSX.Element} The rendered component.
 */
export default function ConnectorSidePanel(
  properties: ConnectorSidePanelProperties,
): React.JSX.Element {
  const { t, i18n } = useTranslation('sidePanel');
  const navigate = useNavigate();
  const isDevicePortal = localStorage.getItem('activePortal') === 'devices';
  console.log(isDevicePortal, '------------------>');
  const { activePortal } = usePortal();
  const {
    fetchIdFrom,
    id,
    connectors: propertyConnectors,
    currentType: propertyCurrentType,
    showHeader,
    showActions,
    onEditClick,
    chargingPointSn: propertyChargingPointSn,
  } = properties;
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [deviceDetails, setDeviceDetails] = useState<ChargingPointProperties>({
    connectors: [],
  });
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [tariffPanelId, setTariffPanelId] = useState<string | null>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });

  const { brandData } = useBranding();
  const domain = getFirstSubdomain();
  const qrBaseUrl = domain && brandData?.qrCode ? brandData?.qrCode : config.qrCodeUrl;
  const serialNumber = propertyChargingPointSn || deviceDetails.chargingPointSn || '';

  /**
   * Function to handle the QR action.
   * @param {string} deviceSN - The device serial number to show QR for.
   */
  const handleQrAction = (deviceSN: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: t('adhocQrCode', 'AdHoC QR Code'),
      header: true,
      content: <DeviceQrCode handleClose={() => setModalBoxStatus('close')} deviceSN={deviceSN} />,
      notificationModal: true,
    });
  };

  /**
   * Function to handle copying link to clipboard.
   * @param {string} link - The link to copy.
   */
  const handleCopyLink = (link: string) => {
    if (!link) return;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        dispatch(
          setNotificationMessage({
            message: t('linkCopied', 'Link copied'),
            status: true,
            type: 'success',
            code: 200,
          }),
        );
      })
      .catch((error: any) => {
        console.error('Failed to copy link: ', error);
      });
  };

  const isExternalData = !!propertyConnectors;

  const deviceId = React.useMemo(() => {
    return fetchIdFrom === 'queryParams' ? searchParameters.get('connectorDeviceId') : id;
  }, [fetchIdFrom, searchParameters, id]);

  /**
   * Function to fetch device details by ID.
   * @param {string} targetDeviceId - The target device ID.
   * @returns {Promise<void>} A promise that resolves when details are fetched.
   */
  const handleFetchDeviceDetails = React.useCallback(
    async (targetDeviceId: string) => {
      setIsLoader(true);
      try {
        const response = await deviceApi.getChargingPointById(targetDeviceId);
        setDeviceDetails(response?.data);
      } catch (error: any) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      } finally {
        setIsLoader(false);
      }
    },
    [dispatch, t],
  );

  useEffect(() => {
    if (!deviceId) return;
    // Only fetch if connectors are not external, OR if we don't have the chargingPointSn passed down
    if (!isExternalData || !propertyChargingPointSn) {
      setDeviceDetails({ connectors: [] });
      handleFetchDeviceDetails(deviceId);
    }
  }, [deviceId, handleFetchDeviceDetails, isExternalData, propertyChargingPointSn]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  const connectors = isExternalData ? propertyConnectors : deviceDetails.connectors;
  const currentType = isExternalData ? propertyCurrentType : deviceDetails.currentType;

  console.log(connectors, 'connectors');

  return (
    <div
      className={`connector-device-wrap ${showHeader === false ? 'embedded' : ''}`}
      dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      {showHeader !== false && (
        <div className="side-header">
          <p className="title">{t('connectorDetails', 'Connector Details')}</p>
        </div>
      )}

      <div className="device-side-panel-container">
        <div className="connector-card-wrapper" style={{ gridColumn: '1 / -1', width: '100%' }}>
          {(connectors || []).map((connector: Connector, index: number) => {
            const connectorType = Array.isArray(connector.connectorType)
              ? connector.connectorType.join(',')
              : connector.connectorType || '';
            const connectorStatus = connector.status || 'unknown';
            const ConnectorIcon =
              connectorIcons[connectorType as keyof typeof connectorIcons] || CCS2Icon;

            const connectorId = connector.connectorId || index + 1;
            const finalDeviceId = serialNumber ? `${serialNumber}_${connectorId}` : '';
            const connectorQrValue = finalDeviceId
              ? `${qrBaseUrl}?deviceId=${finalDeviceId}${domain ? `&domain=${domain}` : ''}`
              : '';

            const canAddTariff =
              (activePortal === PortalType.NETWORKS || activePortal === PortalType.PARTNER) &&
              (permissionCheck('partnerTariffAdd') || permissionCheck('cpoTariffAdd'));

            return (
              <div className="connector-item-wrapper" key={connector._id || index}>
                <div className="connector-header">
                  <h4>
                    {t('connector', 'Connector')} {connector.connectorId || index + 1}
                  </h4>
                  {!isDevicePortal && (
                    <div className="tariff-info">
                      {connector.tariffName ? (
                        <>
                          <span className="tariff-label">{t('tariffName', 'Tariff Name')}: </span>
                          <span
                            role="presentation"
                            className="hyperlink"
                            onClick={() =>
                              setTariffPanelId(
                                (connector as any).tariffId || connector.tariffName || null,
                              )
                            }>
                            {connector.tariffName}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="tariff-not-applied">
                            {t('tariffNotApplied', 'Tariff Not Applied')}
                          </span>
                          {canAddTariff && (
                            <span className="add-tariff-btn-wrap">
                              <MyButton
                                label={t('addTariff', 'Add Tariff')}
                                buttonType="primary"
                                onClickFunc={() => navigate('/ev-admin/tariffs/add-tariff')}
                              />
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="csp-card">
                  <div className="csp-card-top">
                    <div className="csp-card-icon">
                      <ConnectorIcon />
                    </div>
                    <div className="csp-card-title-area">
                      <h4>{connectorType || '-'}</h4>
                      <p>
                        {currentType || '-'}{' '}
                        {typeof connector.connectorRating === 'object' &&
                        connector.connectorRating !== null
                          ? connector.connectorRating.capacityKW || '-'
                          : connector.connectorRating || '-'}
                        {connector.unit || '-'}
                      </p>
                    </div>
                    <div
                      className={`csp-card-type-badge ${
                        connector.currentType
                          ? `${connector.currentType.toLowerCase()}-badge`
                          : 'unknown-badge'
                      }`}>
                      {connector.currentType ? connector.currentType.toUpperCase() : '-'}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        className={`csp-card-badge ${connectorStatus
                          .replace(/\s+/g, '')
                          .replace('SuspendedEV', 'suspendedEv')
                          .replace('SuspendedEVSE', 'suspendedEvse')
                          .toLowerCase()}`}>
                        {connectorStatus.toUpperCase()}
                      </div>
                      {showActions && onEditClick && (
                        <div className="connector-edit-action">
                          <MyButton
                            label={t('edit', 'Edit')}
                            buttonType="primary-outline"
                            onClickFunc={() => onEditClick(connector, index)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="csp-card-divider" />

                  <div className="csp-card-bottom">
                    <div className="csp-col">
                      <p className="csp-label">Connector Format</p>
                      <p className="csp-value">{connector.connectorFormat || '-'}</p>
                    </div>

                    <div className="csp-col">
                      <p className="csp-label">Max Voltage</p>
                      <p className="csp-value">
                        {connector.maxVoltage !== undefined &&
                        connector.maxVoltage !== null &&
                        connector.maxVoltage !== ''
                          ? ToFixedValue(connector.maxVoltage, 2)
                          : '-'}{' '}
                        V
                      </p>
                    </div>

                    <div className="csp-col">
                      <p className="csp-label">Max Current</p>
                      <p className="csp-value">
                        {connector.maxCurrent !== undefined &&
                        connector.maxCurrent !== null &&
                        connector.maxCurrent !== ''
                          ? ToFixedValue(connector.maxCurrent, 2)
                          : '-'}{' '}
                        A
                      </p>
                    </div>
                    <div className="csp-col">
                      <p className="csp-label">Max Power</p>
                      <p className="csp-value">
                        {connector.maxPower !== undefined &&
                        connector.maxPower !== null &&
                        connector.maxPower !== ''
                          ? ToFixedValue(connector.maxPower, 3)
                          : '-'}{' '}
                        Kw
                      </p>
                    </div>
                  </div>

                  <div className="csp-card-divider" />

                  <div className="csp-card-adhoc">
                    <div className="csp-col adhoc-url-col">
                      <div className="adhoc-url-label-row">
                        <p className="csp-label">AdHoC URL</p>
                        {connectorQrValue && (
                          <button
                            type="button"
                            className="qr-icon-btn"
                            onClick={() => handleQrAction(finalDeviceId)}>
                            <QRIcon />
                          </button>
                        )}
                      </div>
                      <div className="adhoc-url-value-wrap">
                        <button
                          type="button"
                          className="link-icon-circle"
                          onClick={() => handleCopyLink(connectorQrValue)}>
                          <LinkIcon />
                        </button>
                        {connectorQrValue ? (
                          <a
                            href={connectorQrValue}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="csp-value hyperlink adhoc-link">
                            {connectorQrValue}
                          </a>
                        ) : (
                          <p className="csp-value">-</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tariff Side Panel Overlay */}
      {tariffPanelId && (
        <SidePaneModel
          open={!!tariffPanelId}
          closePanel={() => setTariffPanelId(null)}
          content={
            <TariffSidePanel
              id={tariffPanelId}
              fetchIdFrom="props"
              isPartner={activePortal === PortalType.PARTNER}
            />
          }
          position="right"
          preventQueryClear
          title="Tariff"
          url={`/ev-admin/tariffs/view/${tariffPanelId}`}
          urlNewTab={`/ev-admin/tariffs/view/${tariffPanelId}`}
        />
      )}
    </div>
  );
}
